import BaseBehavior from '../../../framework/base/base';
import {
	initBehaviors,
	destroyBehaviors,
	resetBehaviorNames
} from '../../../shared/utilities/behaviors';
import { removeComments } from '../../../shared/utilities/comments';
import { initScripts } from '../../../shared/utilities/scripts';
import { getBreakpoint } from '../../../shared/utilities/screenSize';
import { formatWebinarDate } from '../../templates/dcf-manager/dcf-webinar';
import {
	isDevice,
	deviceDetection,
	addDeviceDocumentClass,
	removeDeviceDocumentClass
} from '../../../shared/utilities/deviceDetection';
import { onEffectEndEvent } from '../../../shared/utilities/effectsEndEventHandler';
import { headroom } from '../../../shared/utilities/headroom';
import {
	addTrapFocusListener,
	removeTrapFocusListener
} from '../../../shared/utilities/trap-focus';

const classes = {
	open: 'v2-off-canvas--open'
};

let scrollTopVal;

const modifierClasses = {
	wide: 'v2-off-canvas--wide',
	left: 'v2-off-canvas--left',
	ready: 'v2-off-canvas--ready',
	transitionDisabled: 'v2-off-canvas-ph--transition-disabled'
};

export default class OffCanvas extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();
		this.addEventListeners();
		this.addSubscriptions();
		this.publish('/off-canvas/ready');
	}

	addEventListeners() {
		this.addEventListener(this.dom.overlay, 'click.overlay', e => {
			e.preventDefault();
			this.close();
		});

		this.addEventListener(this.dom.close, 'click.close', e => {
			e.preventDefault();
			this.close();
		});
	}

	removeEventListeners() {
		this.removeEventListener(this.dom.overlay, 'click.overlay');
		this.removeEventListener(this.dom.close, 'click.close');
	}

	shiftOffCanvasDown(referenceNode) {
		const offset = referenceNode.offsetHeight;
		this.element.style.transform = `translateY(${offset}px)`;
		this.element.style.height = `${window.innerHeight - offset}px`;
	}

	adjustForTopESMA() {
		const topESMA = document.querySelector('.v2-cookie-disclaimer--top');
		// check if esma disclaimer dom is present which is aligned on Top.
		if (topESMA) {
			this.shiftOffCanvasDown(topESMA);
			this.subscribe('cookie/height/change', this.shiftOffCanvasDown.bind(this, topESMA));
		}
	}

	addSubscriptions() {
		this.subscribe('/off-canvas/open', configData => {
			if (configData.target) {
				this.adjustForTopESMA();
				this.open(configData);
			}
		});

		this.subscribe('/side-nav/navigate', this.close.bind(this));
	}

	removeSubscriptions() {
		this.unsubscribe('/off-canvas/open');
		this.unsubscribe('cookie/height/change');
	}

	addKeyListeners() {
		this.addEventListener(document, 'keyup', e => {
			if (e.keyCode === 27) {
				this.close();
			}
		});

		addTrapFocusListener(this, { selector: '.v2-off-canvas__content' });
	}

	removeKeyListeners() {
		this.removeEventListener(document, 'keyup');
		removeTrapFocusListener(this);
	}

	open({ target, scriptsTarget, wide, float, breakpoints, parentComponent }) {
		const breakpointsList = breakpoints && breakpoints.split(',');
		const element = document.getElementById(target);
		const offCanvasPh = this.element.querySelector('.v2-off-canvas-ph');

		if (!element || (breakpointsList && !breakpointsList.includes(getBreakpoint()))) {
			return;
		}
		while (this.dom.content.firstChild) {
			this.dom.content.removeChild(this.dom.content.firstChild);
		}

		let targetHtml;
		if (element.nodeName === 'SCRIPT') {
			targetHtml = element.innerHTML;
			this.dom.content.innerHTML = targetHtml;
		} else {
			targetHtml = removeComments(element.outerHTML).replace(
				/(<[^>]*\s)(id|for)=("|')/g,
				'$1$2=$3' + this.instanceId + '-'
			);
			this.dom.content.innerHTML = targetHtml;
		}

		resetBehaviorNames({ context: this.dom.content });
		initBehaviors({ context: this.dom.content });
		initScripts(this.dom.content, scriptsTarget);

		Object.keys(modifierClasses).map(k => document.body.classList.remove(modifierClasses[k]));

		if (wide) {
			document.body.classList.add(modifierClasses.wide);
		}

		const canvasContent = this.element.querySelector('.v2-off-canvas__content');
		if (canvasContent) {
			canvasContent.scrollTop = 0;
		}

		if (float === 'left') {
			offCanvasPh.classList.add(modifierClasses.transitionDisabled);
			document.body.classList.add(modifierClasses.left);
			document.documentElement.classList.add('v2-overflow-hidden');
			// execute restyle to fix issue https://stackoverflow.com/questions/11131875/what-is-the-cleanest-way-to-disable-css-transition-effects-temporarily
			// eslint-disable-next-line
			window.getComputedStyle(offCanvasPh).opacity;
		}

		if (isDevice(deviceDetection.ios)) {
			scrollTopVal =
				window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
			document.body.style.top = -scrollTopVal + 'px';
			addDeviceDocumentClass(deviceDetection.ios);
			if (headroom.instance) headroom.instance.destroy();
		}
		// If it's a DCF form specific for webinars/events
		if (this.dom.content.querySelector('[data-webinar]')) {
			formatWebinarDate(this.dom.content);
		}

		offCanvasPh.classList.remove(modifierClasses.transitionDisabled);

		document.body.classList.add(modifierClasses.ready, classes.open);

		this.removeNavigationTrapFocus(parentComponent);
		this.addKeyListeners();

		if (breakpoints && breakpoints.length > 0) {
			this.publishBreakpointOnResize();
			this.subscribe('SITE.BREAKPOINT', bp => {
				if (!breakpoints.includes(bp)) {
					this.close();
				}
			});
		}
	}

	close() {
		document.body.classList.remove(classes.open);
		document.documentElement.classList.remove('v2-overflow-hidden');

		onEffectEndEvent(this.element, () => {
			Object.keys(modifierClasses).forEach(key => {
				document.body.classList.remove(modifierClasses[key]);
			});
		});

		removeDeviceDocumentClass(deviceDetection.ios);
		this.removeKeyListeners();
		this.unsubscribe('SITE.BREAKPOINT');
		this.unsubscribe('cookie/height/change');

		if (isDevice(deviceDetection.ios)) {
			window.scrollTo({ top: scrollTopVal });
			document.body.style.top = '';
			if (headroom.instance) headroom.instance.init();
		}

		this.publish('/off-canvas/close');
		if (this.parentComponent === 'navigation') {
			// If the off-canvas was opened from the navigation, on Close we need to add the trap focus listener
			this.publish('/navigation/addTrapFocus');
			this.parentComponent = null;
		}
		destroyBehaviors({ context: this.dom.content });
	}

	removeNavigationTrapFocus(parentComponent) {
		if (parentComponent === 'navigation') {
			// If the off-canvas is opened from the navigation, we need to remove the trap focus listener
			this.publish('/navigation/removeTrapFocus', true);
			this.parentComponent = 'navigation';
		}
	}

	cleanup() {
		this.removeEventListeners();
		this.removeSubscriptions();
		this.removeKeyListeners();
	}
}
